import { memo, useCallback, useMemo, useRef, useState } from 'react'
import { renderToString } from 'react-dom/server'
import { styled } from 'styled-components'
import { Pagination, Navigation as SwiperNavigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { PaginationOptions, Swiper as SwiperType } from 'swiper/types'
import { ChevronBackwardIcon } from '../../svg-components'
import { ScreenQuery } from '../../utilities'
import { DESKTOP_OVERLAY_HORIZONTAL_PADDING } from '../overlay'
import { StrapiImage } from '../strapi-image'
import { StrapiVideo } from '../strapi-video'
import { Case } from './case-card'

const ButtonContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  width: fit-content;
  flex-direction: column;
  justify-content: center;
`
const LeftButtonContainer = styled(ButtonContainer)`
  left: 0;
`
const RightButtonContainer = styled(ButtonContainer)`
  right: 0;
`
const NavigationButton = styled.button`
  background-color: ${({ theme }) => theme.colors.container.lowest};
  z-index: 101;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  &:hover {
    opacity: 0.8;
    cursor: pointer;
  }
  path {
    fill: black;
  }
`
const PrevButton = styled(NavigationButton)`
  left: 0;
  svg {
    transform: rotate(180deg);
  }
  margin-left: ${({ theme }) => theme.sizes.spacings.sm}px;
`
const NextButton = styled(NavigationButton)`
  right: 0;
  margin-right: ${({ theme }) => theme.sizes.spacings.sm}px;
`

const StyledSwiper = styled(Swiper)`
  height: 100%;
  ${ScreenQuery.desktop} {
    min-width: 100%;
    max-width: calc((100vw - ${DESKTOP_OVERLAY_HORIZONTAL_PADDING}px) / 2);
  }
  .swiper-pagination {
    position: absolute;
    top: 0;
    height: fit-content;
  }
`
const StyledSwiperSlide = styled(SwiperSlide)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.container.highest};
`
const StyledStrapiImage = styled(StrapiImage)`
  object-fit: contain;
  height: 100%;
  > div {
    height: 100%;
  }
`
const StyledStrapiVideo = styled(StrapiVideo)`
  width: fit-content;
  height: 100%;
  width: 100%;
`
const FractionTextContainer = styled.div`
  width: 100%;
  position: absolute;
  top: 0;
  // cannot use theme here because renderToString cannot access it
  color: white;
  font-size: 20px;
  background: linear-gradient(
    180deg,
    rgba(24, 24, 27, 0.8) 0%,
    rgba(24, 24, 27, 0) 100%
  );
`
type Props = {
  images: Case['images']
  videos: Case['videos']
}

export const CaseDetailOverlaySwiper = memo<Props>(({ images, videos }) => {
  const swiperRef = useRef<SwiperType | null>(null)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(
    (images?.length ?? 0) + (videos?.length ?? 0) <= 1,
  )

  const pagination: PaginationOptions = useMemo(() => {
    return {
      clickable: false,
      type: 'fraction',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      renderFraction(currentClass, totalClass) {
        // renderFraction expects a string, so we need to convert the JSX to string
        // the className is used by Swiper to inject the current and total slide numbers
        return renderToString(
          <FractionTextContainer>
            <span className={currentClass}> </span>
            <span> / </span>
            <span className={totalClass}> </span>
          </FractionTextContainer>,
        )
      },
    }
  }, [])
  const onBeforeInit = useCallback((swiper: SwiperType) => {
    swiperRef.current = swiper
  }, [])
  const handlePrevButtonClick = useCallback(() => {
    swiperRef.current?.slidePrev()
  }, [])
  const handleNextButtonClick = useCallback(() => {
    swiperRef.current?.slideNext()
  }, [])
  const handleSlideChange = useCallback((swiper: SwiperType) => {
    setIsBeginning(swiper.isBeginning)
    setIsEnd(swiper.isEnd)
  }, [])

  return (
    <StyledSwiper
      pagination={pagination}
      modules={[SwiperNavigation, Pagination]}
      onSlideChange={handleSlideChange}
      onBeforeInit={onBeforeInit}
    >
      {!isBeginning && (
        <LeftButtonContainer>
          <PrevButton onClick={handlePrevButtonClick}>
            <ChevronBackwardIcon />
          </PrevButton>
        </LeftButtonContainer>
      )}
      {!isEnd && (
        <RightButtonContainer>
          <NextButton onClick={handleNextButtonClick}>
            <ChevronBackwardIcon />
          </NextButton>
        </RightButtonContainer>
      )}
      {(images ?? []).map((image, idx) => {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <StyledSwiperSlide key={idx}>
            <StyledStrapiImage strapiImage={image ?? null} />
          </StyledSwiperSlide>
        )
      })}
      {(videos ?? []).map((video, idx) => {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <StyledSwiperSlide key={idx}>
            <StyledStrapiVideo strapiVideo={video} />
          </StyledSwiperSlide>
        )
      })}
    </StyledSwiper>
  )
})

CaseDetailOverlaySwiper.displayName = 'CaseDetailOverlaySwiper'
